// ── Zone plate: the card's engraved outline ───────────────────────────────
// Everything here runs ONCE per zone when the page data loads, and the result
// is cached on the zone record. The live refresh loop only ever calls
// projectToPlate(), which is 2 multiplies and 2 adds — it never touches the
// ring again.

import type { PlateKind, ZonePlate } from './zone-activity-types';

/** Plate artwork is drawn in a 100x100 viewBox with this much padding. */
const VIEWBOX = 100;
const PAD_PCT = 0.08;

/** Simplification tolerance, as a fraction of the ring's bbox diagonal. */
const RDP_TOL_PCT = 0.0035;

/** Hard ceiling on plotted vertices — beyond this the detail is sub-pixel. */
const MAX_POINTS = 90;

const M_PER_DEG_LAT = 111_320;

/** Radii vary by less than this fraction of the mean → an auto-generated circular buffer. */
const CIRCULARITY_CV = 0.08;

/** Simplified ring this small that fills its bbox this well → an axis-aligned box. */
const BOX_MAX_POINTS = 6;
const BOX_AREA_RATIO = 0.92;

/** Perpendicular distance from p to the line a→b, in corrected (x, y) space. */
function perpDist(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const len2 = dx * dx + dy * dy;
  if (len2 === 0) return Math.hypot(px - ax, py - ay);
  let t = ((px - ax) * dx + (py - ay) * dy) / len2;
  t = Math.max(0, Math.min(1, t));
  return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
}

/** Ramer–Douglas–Peucker, iterative (a recursive version blows the stack on big rings). */
function rdp(xs: number[], ys: number[], tol: number): number[] {
  const n = xs.length;
  if (n <= 2) return xs.map((_, i) => i);

  const keep = new Uint8Array(n);
  keep[0] = 1;
  keep[n - 1] = 1;

  const stack: Array<[number, number]> = [[0, n - 1]];
  while (stack.length) {
    const [first, last] = stack.pop()!;
    let maxDist = -1;
    let maxIdx  = -1;
    for (let i = first + 1; i < last; i++) {
      const d = perpDist(xs[i], ys[i], xs[first], ys[first], xs[last], ys[last]);
      if (d > maxDist) { maxDist = d; maxIdx = i; }
    }
    if (maxIdx !== -1 && maxDist > tol) {
      keep[maxIdx] = 1;
      stack.push([first, maxIdx], [maxIdx, last]);
    }
  }

  const out: number[] = [];
  for (let i = 0; i < n; i++) if (keep[i]) out.push(i);
  return out;
}

/** Evenly decimate to at most `max` entries, always keeping the first. */
function decimate<T>(items: T[], max: number): T[] {
  if (items.length <= max) return items;
  const out: T[] = [];
  const stride = items.length / max;
  for (let i = 0; i < max; i++) out.push(items[Math.floor(i * stride)]);
  return out;
}

/**
 * Min/max via a plain loop, not `Math.min(...arr)`. A spread argument list has
 * an engine-dependent ceiling well below what a densely-digitized imported
 * zone can have, and this ring is unsimplified input — RDP hasn't trimmed it
 * yet at any of this function's call sites.
 */
function minMax(arr: number[] | Float64Array): [number, number] {
  let lo = Infinity, hi = -Infinity;
  for (let i = 0; i < arr.length; i++) {
    const v = arr[i];
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  return [lo, hi];
}

/** Signed area (shoelace) — magnitude only is used here. */
function ringArea(xs: number[], ys: number[]): number {
  let a = 0;
  for (let i = 0, j = xs.length - 1; i < xs.length; j = i++) {
    a += xs[j] * ys[i] - xs[i] * ys[j];
  }
  return Math.abs(a / 2);
}

function classify(xs: number[], ys: number[], simplifiedCount: number): PlateKind {
  const n = xs.length;
  if (n < 3) return 'circle';

  let cx = 0, cy = 0;
  for (let i = 0; i < n; i++) { cx += xs[i]; cy += ys[i]; }
  cx /= n; cy /= n;

  let rSum = 0;
  const radii = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const r = Math.hypot(xs[i] - cx, ys[i] - cy);
    radii[i] = r;
    rSum += r;
  }
  const rMean = rSum / n;
  if (rMean > 0) {
    let varSum = 0;
    for (let i = 0; i < n; i++) varSum += (radii[i] - rMean) ** 2;
    const cv = Math.sqrt(varSum / n) / rMean;
    // A Geotab address buffer is a many-sided regular polygon: near-zero radius spread.
    if (cv < CIRCULARITY_CV && n >= 8) return 'circle';
  }

  const [minX, maxX] = minMax(xs);
  const [minY, maxY] = minMax(ys);
  const bboxArea = (maxX - minX) * (maxY - minY);
  if (
    simplifiedCount <= BOX_MAX_POINTS &&
    bboxArea > 0 &&
    ringArea(xs, ys) / bboxArea > BOX_AREA_RATIO
  ) return 'box';

  return 'silhouette';
}

/** Fallback artwork for rings that are degenerate (no area, too few points). */
function circlePath(): string {
  const r = (VIEWBOX / 2) * (1 - PAD_PCT * 2);
  const c = VIEWBOX / 2;
  // Two arcs — a full circle can't be expressed with a single arc command.
  return `M ${c - r} ${c} A ${r} ${r} 0 1 0 ${c + r} ${c} A ${r} ${r} 0 1 0 ${c - r} ${c} Z`;
}

/**
 * Build the cached SVG plate for a ring given in degrees.
 *
 * Longitude is multiplied by cos(centre latitude) so a zone at high latitude is
 * not drawn horizontally stretched; the same correction is baked into the
 * returned transform, so live vehicle marks land in the right place.
 */
export function buildPlate(lat: Float64Array, lng: Float64Array): ZonePlate {
  const n = lat.length;
  const [latLo, latHi] = n ? minMax(lat) : [0, 0];
  const centreLat = n ? (latLo + latHi) / 2 : 0;
  const cosLat = Math.max(0.05, Math.cos((centreLat * Math.PI) / 180));

  // Corrected space: x is longitude scaled so one x unit covers the same ground
  // distance as one y (latitude) unit.
  const xs: number[] = new Array(n);
  const ys: number[] = new Array(n);
  for (let i = 0; i < n; i++) { xs[i] = lng[i] * cosLat; ys[i] = lat[i]; }

  const [minX, maxX] = minMax(xs);
  const [minY, maxY] = minMax(ys);
  const w = maxX - minX;
  const h = maxY - minY;
  const diag = Math.hypot(w, h);

  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;
  const usable = VIEWBOX * (1 - PAD_PCT * 2);
  const scale  = diag > 0 && Math.max(w, h) > 0 ? usable / Math.max(w, h) : 0;

  const tx: ZonePlate['tx'] = [scale, cx, cy, cosLat];

  // Mean radius in metres — captioned on circular buffer plates.
  let rSum = 0;
  for (let i = 0; i < n; i++) rSum += Math.hypot(xs[i] - cx, ys[i] - cy);
  const radiusM = n ? Math.round((rSum / n) * M_PER_DEG_LAT) : 0;

  if (n < 3 || diag === 0 || scale === 0) {
    return { d: circlePath(), kind: 'circle', tx, radiusM };
  }

  const keptIdx  = rdp(xs, ys, diag * RDP_TOL_PCT);
  const finalIdx = decimate(keptIdx, MAX_POINTS);
  const kind     = classify(xs, ys, keptIdx.length);

  let d = '';
  for (let k = 0; k < finalIdx.length; k++) {
    const i = finalIdx[k];
    const px = (50 + (xs[i] - cx) * scale).toFixed(2);
    // SVG y grows downward; latitude grows upward.
    const py = (50 - (ys[i] - cy) * scale).toFixed(2);
    d += (k === 0 ? 'M ' : ' L ') + px + ' ' + py;
  }
  d += ' Z';

  return { d, kind, tx, radiusM };
}

/**
 * Map a live coordinate into the zone's plate viewBox. Returns null when the
 * plate has no usable scale (degenerate ring), so callers can skip the mark.
 */
export function projectToPlate(
  tx: ZonePlate['tx'], lat: number, lng: number,
): { x: number; y: number } | null {
  const [scale, cx, cy, cosLat] = tx;
  if (!scale) return null;
  return {
    x: 50 + (lng * cosLat - cx) * scale,
    y: 50 - (lat - cy) * scale,
  };
}
